import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFormField } from "../../../../store/schedulingReducer";
import CustomCalendar from "./CustomCalendar";

const ScheduleTimeFields = () => {
  const dispatch = useDispatch();
  const { form, validationErrors } = useSelector((state) => state.scheduling);
  const { startDate, startTime, endDate, endTime } = form || {};
  const [localError, setLocalError] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];
  const startDateValue = startDate || "";
  const endDateValue = endDate || "";

  const compareTimes = (time1, time2) => {
    if (!time1 || !time2) return 0;
    const [h1, m1] = time1.split(":").map(Number);
    const [h2, m2] = time2.split(":").map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    return minutes1 - minutes2;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const validateTime = (name, value) => {
    if (!value) {
      if (name === "startTime") {
        setStartTimeError("");
      } else if (name === "endTime") {
        setEndTimeError("");
      }
      return true;
    }
    
    if (name === "startTime" && startDateValue === today) {
      const currentTime = getCurrentTime();
      if (compareTimes(value, currentTime) < 0) {
        setStartTimeError("Start time cannot be in the past");
        return false;
      }
      setStartTimeError("");
    }
    
    if (name === "endTime") {
      if (endDateValue === today || (startDateValue === today && startDateValue === endDateValue)) {
        const currentTime = getCurrentTime();
        const minEndTime = getMinEndTime();
        const minTimeToUse = minEndTime || currentTime;
        if (compareTimes(value, minTimeToUse) < 0) {
          setEndTimeError("End time cannot be in the past or before start time");
          return false;
        }
        setEndTimeError("");
      }
    }
    
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (!validateTime(name, value)) {
      const inputRef = name === "startTime" ? startTimeInputRef : endTimeInputRef;
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      dispatch(updateFormField({ key: name || e.target.name, value: "" }));
      return;
    }
    
    dispatch(updateFormField({ key: name || e.target.name, value }));
    setLocalError("");
    if (name === "startTime") {
      setStartTimeError("");
    } else if (name === "endTime") {
      setEndTimeError("");
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    if (!validateTime(name, value)) {
      const inputRef = name === "startTime" ? startTimeInputRef : endTimeInputRef;
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      dispatch(updateFormField({ key: name || e.target.name, value: "" }));
    }
  };

  const handleTimeRowClick = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        if (inputRef.current && inputRef.current.showPicker) {
          try {
            inputRef.current.showPicker();
          } catch (err) {
            console.debug("showPicker() not available or failed:", err.message);
          }
        }
      }, 0);
    }
  };

  const getMinStartTime = () => {
    if (startDateValue === today) {
      return getCurrentTime();
    }
    return "";
  };

  const getMinEndTime = () => {
    const currentTime = getCurrentTime();
    
    if (endDateValue === today) {
      if (startDateValue === endDateValue && startTime) {
        const [startHour, startMin] = startTime.split(":").map(Number);
        const [currentHour, currentMin] = currentTime.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const currentMinutes = currentHour * 60 + currentMin;
        
        if (startMinutes >= currentMinutes) {
          const minMinutes = startMin + 1;
          const minHour = minMinutes >= 60 ? startHour + 1 : startHour;
          const finalMin = minMinutes >= 60 ? minMinutes - 60 : minMinutes;
          return `${String(minHour).padStart(2, '0')}:${String(finalMin).padStart(2, '0')}`;
        } else {
          return currentTime;
        }
      }
      return currentTime;
    }
    
    if (startDateValue === today && startDateValue === endDateValue && startTime) {
      const [startHour, startMin] = startTime.split(":").map(Number);
      const [currentHour, currentMin] = currentTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const currentMinutes = currentHour * 60 + currentMin;
      
      if (startMinutes >= currentMinutes) {
        const minMinutes = startMin + 1;
        const minHour = minMinutes >= 60 ? startHour + 1 : startHour;
        const finalMin = minMinutes >= 60 ? minMinutes - 60 : minMinutes;
        return `${String(minHour).padStart(2, '0')}:${String(finalMin).padStart(2, '0')}`;
      } else {
        return currentTime;
      }
    }
    
    if (startDateValue === endDateValue && startTime) {
      const [startHour, startMin] = startTime.split(":").map(Number);
      const minMinutes = startMin + 1;
      const minHour = minMinutes >= 60 ? startHour + 1 : startHour;
      const finalMin = minMinutes >= 60 ? minMinutes - 60 : minMinutes;
      return `${String(minHour).padStart(2, '0')}:${String(finalMin).padStart(2, '0')}`;
    }
    
    return "";
  };

  const minStartTime = getMinStartTime();
  const minEndTime = getMinEndTime();

  useEffect(() => {
    if (startDateValue === today && startTime) {
      const currentTime = getCurrentTime();
      if (compareTimes(startTime, currentTime) < 0) {
        dispatch(updateFormField({ key: "startTime", value: "" }));
        if (startTimeInputRef.current) {
          startTimeInputRef.current.value = "";
        }
        setStartTimeError("");
      } else {
        setStartTimeError("");
      }
    } else {
      setStartTimeError("");
    }
  }, [startDateValue, today, startTime, dispatch]);

  useEffect(() => {
    if ((endDateValue === today || (startDateValue === today && startDateValue === endDateValue)) && endTime) {
      const currentTime = getCurrentTime();
      const minTimeToUse = minEndTime || currentTime;
      if (compareTimes(endTime, minTimeToUse) < 0) {
        dispatch(updateFormField({ key: "endTime", value: "" }));
        if (endTimeInputRef.current) {
          endTimeInputRef.current.value = "";
        }
        setEndTimeError("");
      } else {
        setEndTimeError("");
      }
    } else {
      setEndTimeError("");
    }
  }, [endDateValue, startDateValue, today, endTime, minEndTime, dispatch]);

  useEffect(() => {
    if (startDateValue && endDateValue && startDateValue === endDateValue) {
      if (startTime && endTime && startTime === endTime) {
        setLocalError("Start time and end time cannot be the same");
      } else if (startTime && endTime) {
        const [startHour, startMin] = startTime.split(":").map(Number);
        const [endHour, endMin] = endTime.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        
        if (endMinutes <= startMinutes) {
          setLocalError("End time must be after start time");
        } else {
          setLocalError("");
        }
      } else {
        setLocalError("");
      }
    } else {
      setLocalError("");
    }
  }, [startDateValue, endDateValue, startTime, endTime]);

  const errorMessage = validationErrors?.endTime || endTimeError || localError || "";

  return (
    <div className="space-y-4">
      <div>
        <label 
          htmlFor="startDate"
          className="block text-sm font-medium text-gray-700 mb-2 cursor-pointer"
        >
          Active From (Date & Time)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <CustomCalendar
            value={startDateValue}
            onChange={handleChange}
            minDate={today}
            name="startDate"
          />
          <div className="w-full relative">
            {startTimeError && (
              <p className="absolute -top-5 left-0 text-sm text-red-500 whitespace-nowrap">{startTimeError}</p>
            )}
            <div 
              className="w-full cursor-pointer"
              onClick={() => handleTimeRowClick(startTimeInputRef)}
            >
              <input
                id="startTime"
                ref={startTimeInputRef}
                type="time"
                name="startTime"
                value={startTime || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                min={minStartTime}
                className={`w-full px-3 py-2 border-[0.5px] rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 cursor-pointer pointer-events-none ${
                  startTimeError ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label 
          htmlFor="endDate"
          className="block text-sm font-medium text-gray-700 mb-2 cursor-pointer"
        >
          Active To (Date & Time)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <CustomCalendar
            value={endDateValue}
            onChange={handleChange}
            minDate={startDateValue || today}
            name="endDate"
          />
          <div className="w-full relative">
            {errorMessage && (
              <p className="absolute -top-5 left-0 text-sm text-red-500 whitespace-nowrap">{errorMessage}</p>
            )}
            <div 
              className="w-full cursor-pointer"
              onClick={() => handleTimeRowClick(endTimeInputRef)}
            >
              <input
                id="endTime"
                ref={endTimeInputRef}
                type="time"
                name="endTime"
                value={endTime || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                min={minEndTime}
                className={`w-full px-3 py-2 border-[0.5px] rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 cursor-pointer pointer-events-none ${
                  errorMessage ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTimeFields;